import ChatSingleTask from "@/components/ChatSingleTask/chatSingleTask.component";
import CustomLoader from "@/components/CustomLoader/customLoader.component";
import ErrorBoundary from "@/components/ErrorBoundary/errorBoundary.component";
import SingleTaskViewSheetNormal from "@/components/SingleTaskViewSheet/singleTaskViewSheetNormal.component";
import { ROUTE_TASKS } from "@/constants/routes.constants";
import { useMediaQuery } from "@/hooks/AppContext/mediaQuery";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import {
  ResizableHandle,
  ResizablePanel,
} from "@/shadcn/components/ui/resizable";
import { EditedTask, FullTask, NewTaskTag } from "@/types";
import { formatDueDate, getNewTaskTagByID } from "@/utils/utils";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRightFromLine } from "lucide-react";
import React, { useContext, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router";

type SingleTaskViewSheetProps = {
  taskID: number | undefined;
  disableClose?: boolean;
};

const SingleTaskViewSheet: React.FC<SingleTaskViewSheetProps> = ({
  taskID,
  disableClose,
}) => {
  const { userTags, user, singleTask, getFullTask, activeTaskIdRef } =
    useContext(AppContext);
  const controllerRef = useRef(new AbortController());
  const nav = useNavigate();
  const [chatActive, setChatActive] = React.useState(false);

  // Fetch task based on the taskID prop
  useLayoutEffect(() => {
    if (!taskID) return;
    // Update active task ID for this effect
    activeTaskIdRef.current = taskID;

    const controller = new AbortController();
    controllerRef.current = controller;
    const signal = controller.signal;

    getFullTask({ taskID: taskID, signal: signal });

    return () => {
      controllerRef.current.abort();
    };
  }, [taskID, getFullTask, activeTaskIdRef]);

  // #region convertOldTask
  // converts a FullTask to an EditedTask
  function convertOldTask(oldTask: FullTask): EditedTask {
    const id = oldTask.id;
    const assigned_to = oldTask.assignee_id;
    const title = oldTask.title;
    const due_date = oldTask.due_date
      ? formatDueDate(new Date(oldTask.due_date))
      : null;
    const input = oldTask.description ? oldTask.description : "";
    const collaborators = oldTask.collaborators.map(
      (collaborator) => collaborator.id
    );
    const tags: NewTaskTag[] = oldTask.tags
      .map((tag) => {
        const newTag = getNewTaskTagByID(tag.id, userTags);
        if (newTag) {
          return newTag;
        } else {
          console.error("Tag not found", tag);
          return undefined;
        }
      })
      .filter((tag): tag is NewTaskTag => tag !== undefined);
    const parent_id = oldTask.parent_id ? oldTask.parent_id : undefined;
    const status: string = oldTask.status;
    return {
      id,
      assigned_to,
      title,
      due_date,
      input,
      collaborators,
      tags,
      parent_id,
      status,
    };
  }
  // #endregion convertOldTask

  function toggleChat() {
    console.log("toggle chat");
    setChatActive(!chatActive);
  }

  const isNotSmall = useMediaQuery("sm");

  return (
    <>
      {isNotSmall && <ResizableHandle withHandle />}

      <AnimatePresence>
        <>
          {chatActive && taskID && (
            <>
              <ResizablePanel
                id="panel-task-chat"
                order={2}
                className="h-full min-w-[400px] max-w-[600px]"
              >
                <motion.div
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={{
                    hidden: { opacity: 0 },
                    visible: { opacity: 1 },
                  }}
                  transition={{ duration: 0.2 }}
                  className="h-full"
                >
                  <ChatSingleTask taskID={taskID} closeChat={toggleChat} />
                </motion.div>
              </ResizablePanel>
              <ResizableHandle withHandle />
            </>
          )}

          <ResizablePanel
            className="w-full md:w-auto md:min-w-[33%] md:max-w-[55%] !overflow-y-auto max-w-[100vw]"
            defaultSize={isNotSmall ? 33 : 100}
            id="panel-task-view"
            order={3}
          >
            <motion.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={{
                hidden: { opacity: 0, x: 100 },
                visible: { opacity: 1, x: 0 },
              }}
              transition={{ duration: 0.2 }}
              className="w-full"
            >
              <div className="w-full h-full overflow-auto p-3 md:p-5">
                {singleTask.loading && !singleTask.data ? (
                  <div className="m-auto my-20">
                    <CustomLoader />
                  </div>
                ) : singleTask.error ? (
                  <div className="flex justify-between items-center">
                    {singleTask.error === "unauthorized" ? (
                      <div className="flex-grow text-center p-4">
                        <div className="text-red-500 font-semibold text-lg">
                          Looks like you need access to this task.
                        </div>
                        <div className="font-semibold">
                          You're currently signed in as {user.email}
                        </div>
                      </div>
                    ) : singleTask.error === "network_error" ? (
                      <div className="flex-grow text-center text-red-500 font-semibold p-4">
                        Network error. Please try again later.
                      </div>
                    ) : (
                      <div className="flex-grow text-center text-red-500 font-semibold p-4">
                        {singleTask.error}
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="btn btn-primary rounded-sm"
                      onClick={() => {
                        nav(ROUTE_TASKS);
                      }}
                    >
                      <ArrowRightFromLine className="w-5 h-5" />
                    </Button>
                  </div>
                ) : !singleTask.data ? (
                  <div className="flex justify-between items-center">
                    <div className="flex-grow text-center text-red-500 font-semibold p-4">
                      Task not found
                    </div>
                  </div>
                ) : (
                  <ErrorBoundary>
                    <SingleTaskViewSheetNormal
                      key={taskID}
                      task={convertOldTask(singleTask.data)}
                      // auditLogs={singleTask.data.audit_logs}
                      collaborators={singleTask.data.collaborators}
                      subTasks={singleTask.data.children}
                      taskID={taskID}
                      toggleChat={toggleChat}
                      disableClose={disableClose}
                      getFullTask={getFullTask}
                    />
                  </ErrorBoundary>
                )}
              </div>
            </motion.div>
          </ResizablePanel>
        </>
      </AnimatePresence>
    </>
  );
};

export default SingleTaskViewSheet;
